export interface ObservationNavigationContext {
  ids: string[];
  sourcePath: string;
  source: "observations" | "qualityAssurance";
  queryParams?: Record<string, any>;
  totalPages?: number;
  isExternalReviewer?: boolean;
}

export interface ObservationNavigationState {
  observationNavigation?: ObservationNavigationContext;
}

const STORAGE_KEY = "observationNavigationContext";

export const extractObservationNavigationIds = (
  observations: any[] = []
): string[] =>
  Array.from(
    new Set(
      observations
        .map((observation) => observation?.id ?? observation?.original?.id)
        .filter((id) => id !== undefined && id !== null)
        .map(String)
    )
  );

export const buildObservationNavigationContext = (
  observations: any[] = [],
  sourcePath: string,
  options: Omit<ObservationNavigationContext, "ids" | "sourcePath">
): ObservationNavigationContext => ({
  ids: extractObservationNavigationIds(observations),
  sourcePath,
  ...options,
});

export const applyObservationListScopeToQuery = (
  queryParams: Record<string, any>,
  scope: {
    communityId?: string;
    programId?: string;
    kpiState?: {
      redirectFromKPI?: boolean;
      speciesObserved?: boolean;
    } | null;
  }
) => {
  const scopedQueryParams = { ...queryParams };

  if (scope.communityId) {
    scopedQueryParams.communityIds = scope.communityId;

    if (scope.kpiState?.redirectFromKPI) {
      scopedQueryParams.speciesCommunityKpi = scope.communityId;
    } else if (scope.kpiState) {
      scopedQueryParams.objectsCommunityKpi = scope.communityId;
    }
  } else if (scope.programId) {
    scopedQueryParams.programIds = scope.programId;
  }

  if (scope.kpiState?.speciesObserved) {
    scopedQueryParams.specieObserved = true;
  }

  return scopedQueryParams;
};

export const applyQualityAssuranceListScopeToQuery = (
  queryParams: Record<string, any>,
  scope: {
    communityId?: string;
    programId?: string;
  }
) => {
  const scopedQueryParams = { ...queryParams };

  if (scope.programId) {
    scopedQueryParams.programIds = scope.programId.toString();
  }

  if (scope.communityId) {
    scopedQueryParams.communityIds = scope.communityId.toString();
  }

  return scopedQueryParams;
};

export const persistObservationNavigationContext = (
  context?: ObservationNavigationContext
) => {
  if (typeof sessionStorage === "undefined") return;
  if (!context?.ids?.length) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Navigation should continue to work from route state if storage is blocked.
  }
};

const isValidObservationNavigationContext = (
  context: any,
  expectedSource?: ObservationNavigationContext["source"]
): context is ObservationNavigationContext =>
  Boolean(
    context &&
      Array.isArray(context.ids) &&
      context.ids.length > 0 &&
      (context.source === "observations" ||
        context.source === "qualityAssurance") &&
      (!expectedSource || context.source === expectedSource)
  );

export const getStoredObservationNavigationContextForSource = (
  expectedSource?: ObservationNavigationContext["source"]
): ObservationNavigationContext | undefined => {
  if (typeof sessionStorage === "undefined") return undefined;

  try {
    const rawContext = sessionStorage.getItem(STORAGE_KEY);
    if (!rawContext) return undefined;
    const context = JSON.parse(rawContext) as ObservationNavigationContext;
    return isValidObservationNavigationContext(context, expectedSource)
      ? context
      : undefined;
  } catch {
    return undefined;
  }
};

export const getStoredObservationNavigationContext = ():
  | ObservationNavigationContext
  | undefined => getStoredObservationNavigationContextForSource();

export const getObservationNavigationContext = (
  state: ObservationNavigationState | null | undefined,
  expectedSource?: ObservationNavigationContext["source"]
) => {
  const stateContext = state?.observationNavigation;
  return isValidObservationNavigationContext(stateContext, expectedSource)
    ? stateContext
    : getStoredObservationNavigationContextForSource(expectedSource);
};
